package br.com.texeratx.boardgametimeapp.ui.screens.matches

import br.com.texeratx.boardgametimeapp.data.repository.MatchRepository
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.toUI
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class MatchesViewModel(
    private val repository: MatchRepository
) : ScreenModel {

    private var allMatches: List<MatchListItemUI> = emptyList()

    val state = MutableStateFlow(MatchesUiState())

    fun loadMatches() {
        screenModelScope.launch {
            state.value = state.value.copy(isLoading = true, error = null)

            repository.getMatches()
                .fold(
                    onSuccess = { dtoList ->
                        allMatches = dtoList.map { it.toUI() }
                        state.value = state.value.copy(
                            availableGames = allMatches
                                .map { MatchFilterGameOption(it.game.id, it.game.name) }
                                .distinctBy { it.id }
                                .sortedBy { it.name.lowercase() },
                            availablePlayers = allMatches
                                .flatMap { match -> match.players.map { MatchFilterPlayerOption(it.id, it.nickname ?: it.name) } }
                                .distinctBy { it.id }
                                .sortedBy { it.name.lowercase() }
                        )
                        applyFiltersAndSort()
                    },
                    onFailure = {
                        state.value = state.value.copy(
                            isLoading = false,
                            error = it.message
                        )
                    }
                )
        }
    }

    fun onSearch(query: String) {
        state.value = state.value.copy(searchQuery = query)
        applyFiltersAndSort()
    }

    fun updateFilters(filters: MatchesFilter) {
        state.value = state.value.copy(filters = filters)
        applyFiltersAndSort()
    }

    fun changeSort(sort: MatchSortOption) {
        state.value = state.value.copy(sort = sort)
        applyFiltersAndSort()
    }

    fun dismissError() {
        state.value = state.value.copy(error = null)
    }

    private fun applyFiltersAndSort() {
        val currentState = state.value

        val filteredAndSorted = filterAndSortMatches(
            matches = allMatches,
            searchQuery = currentState.searchQuery,
            filters = currentState.filters,
            sort = currentState.sort
        )

        state.value = currentState.copy(
            matches = filteredAndSorted,
            isLoading = false
        )
    }
}

internal fun filterAndSortMatches(
    matches: List<MatchListItemUI>,
    searchQuery: String,
    filters: MatchesFilter,
    sort: MatchSortOption
): List<MatchListItemUI> {
    val normalizedQuery = searchQuery.trim()

    val normalizedStartDate = normalizeApiDateToStartBoundary(filters.startDateApi)
    val normalizedEndDate = normalizeApiDateToEndBoundary(filters.endDateApi)

    val skipDurationFilter = filters.minDuration != null &&
            filters.maxDuration != null &&
            filters.minDuration > filters.maxDuration

    val skipDateFilter = normalizedStartDate != null &&
            normalizedEndDate != null &&
            normalizedStartDate > normalizedEndDate

    return matches
        .filter { match ->
            val matchDateTime = normalizeMatchDateForComparison(match.date)

            val matchesSearch = normalizedQuery.isBlank() ||
                    match.game.name.contains(normalizedQuery, ignoreCase = true) ||
                    match.players.any { player ->
                        player.name.contains(normalizedQuery, ignoreCase = true) ||
                                (player.nickname?.contains(normalizedQuery, ignoreCase = true) == true)
                    }

            val matchesSelectedGames = filters.selectedGameIds.isEmpty() ||
                    filters.selectedGameIds.contains(match.game.id)

            val matchesSelectedPlayers = filters.selectedPlayerIds.isEmpty() ||
                    match.players.any { player -> filters.selectedPlayerIds.contains(player.id) }

            val matchesDuration = when {
                skipDurationFilter -> true
                filters.minDuration != null && match.duration < filters.minDuration -> false
                filters.maxDuration != null && match.duration > filters.maxDuration -> false
                else -> true
            }

            val matchesDate = when {
                skipDateFilter -> true
                matchDateTime == null -> false
                normalizedStartDate != null && matchDateTime < normalizedStartDate -> false
                normalizedEndDate != null && matchDateTime > normalizedEndDate -> false
                else -> true
            }

            matchesSearch &&
                    matchesSelectedGames &&
                    matchesSelectedPlayers &&
                    matchesDuration &&
                    matchesDate
        }
        .let { list ->
            when (sort) {
                MatchSortOption.DATE_DESC -> list.sortedByDescending { normalizeMatchDateForSort(it.date) }
                MatchSortOption.DATE_ASC -> list.sortedBy { normalizeMatchDateForSort(it.date) }
                MatchSortOption.DURATION_DESC -> list.sortedByDescending { it.duration }
                MatchSortOption.DURATION_ASC -> list.sortedBy { it.duration }
            }
        }
}

private fun normalizeApiDateToStartBoundary(dateApi: String?): String? {
    val normalized = normalizeDateApi(dateApi) ?: return null
    return if ('T' in normalized) normalized else "${normalized}T00:00"
}

private fun normalizeApiDateToEndBoundary(dateApi: String?): String? {
    val normalized = normalizeDateApi(dateApi) ?: return null
    return if ('T' in normalized) normalized else "${normalized}T23:59"
}

private fun normalizeMatchDateForComparison(dateApi: String?): String? {
    val normalized = normalizeDateApi(dateApi) ?: return null
    return if ('T' in normalized) normalized else "${normalized}T00:00"
}

private fun normalizeMatchDateForSort(dateApi: String?): String {
    return normalizeMatchDateForComparison(dateApi) ?: ""
}

private fun normalizeDateApi(value: String?): String? {
    val raw = value?.trim().orEmpty()
    if (raw.isBlank()) return null

    val datePart = raw.substringBefore('T')
    if (!isIsoDate(datePart)) return null

    return if ('T' in raw) {
        val timePart = raw.substringAfter('T').take(5)
        if (!isHourMinute(timePart)) {
            "${datePart}T00:00"
        } else {
            "${datePart}T${timePart}"
        }
    } else {
        datePart
    }
}

private fun isIsoDate(value: String): Boolean {
    return value.length == 10 &&
            value[4] == '-' &&
            value[7] == '-' &&
            value.substring(0, 4).toIntOrNull() != null &&
            value.substring(5, 7).toIntOrNull() != null &&
            value.substring(8, 10).toIntOrNull() != null
}

private fun isHourMinute(value: String): Boolean {
    if (value.length != 5 || value[2] != ':') return false
    val hour = value.substring(0, 2).toIntOrNull() ?: return false
    val minute = value.substring(3, 5).toIntOrNull() ?: return false
    return hour in 0..23 && minute in 0..59
}
